/**
 * ir-bbox-sync — reconcile a WASM-returned bbox list against the local IR.
 *
 * The WASM cache is the SERVER-side struct tree; client-only ops
 * (delete-element, set-bbox, create-element) skip the WASM round-trip,
 * so the WASM list still contains entries for locally-deleted structs
 * and stale bboxes for locally-moved structs. This helper merges the
 * two sources of truth so the overlay layer never shows ghosts.
 *
 * Pure module — no DOM access, no I/O. Unit-testable in node.
 *
 * @module ir-bbox-sync
 */
/**
 * Reconcile WASM bboxes against the local IR.
 *
 * Rules:
 *   1. For WASM entries whose structId has a local bbox override in the IR
 *      (drag/resize commit), substitute the IR's bbox.
 *   2. Pass all other WASM entries through unchanged — UNLESS the WASM
 *      entry carries a degenerate (zero/negative-area) bbox AND the IR
 *      has a valid bbox for the same structId. In that case the IR bbox
 *      wins. This is the "Figure-without-MCID" rescue path: WASM derives
 *      bboxes from MCID-anchored RENDER_TEXT / RENDER_IMAGE events, so
 *      a Figure whose image XObject is referenced via /OBJR (annotation
 *      ref) or whose content lacks BDC/MCID brackets emits a zero-bbox
 *      sentinel. The IR's bbox is sourced from /A[/Layout]/BBox and is
 *      authoritative for those struct elems. Without this rescue the
 *      overlay layer skips the zero-bbox entry (overlays.js drops
 *      bw<=0 || bh<=0) and the user sees the card but no overlay.
 *   3. Append IR-side blocks that have a bbox but are absent from the WASM
 *      cache (covers UNDEFINED inserts committed locally before a save).
 *
 * WHY no "drop locally-deleted" rule: The IR is a high-level editor model
 * containing only top-level card-pane blocks; many struct elements in the
 * WASM tree (wrappers, containers, nested list items) were NEVER in the IR
 * and should still be rendered as overlays. A "drop if not in IR" rule
 * would silently erase all those overlays — exactly the bug that motivated
 * this rewrite.
 *
 * The server /api/editor-ir endpoint frequently omits bbox data (optional
 * field); when it does, all WASM boxes pass through unmodified (rule 2)
 * and no entries are appended (rule 3 empty). This is the common case
 * on first load of a completed job.
 */
export function reconcileBboxesWithIr(wasmBoxes, ir, pageNum) {
    if (!ir || !Array.isArray(ir.blocks)) {
        return Array.isArray(wasmBoxes) ? wasmBoxes : [];
    }
    // irBboxById: structId → {page, bbox} for IR blocks with a local bbox override.
    const irBboxById = buildIrBboxIndex(ir);
    const out = [];
    const seenIds = new Set();
    if (Array.isArray(wasmBoxes)) {
        for (const e of wasmBoxes) {
            if (!e?.structId)
                continue;
            seenIds.add(e.structId);
            // Rule 1 (override): substitute IR's bbox when a local override
            // exists. Currently this fires whenever the IR has a bbox for
            // this structId — both for genuine drag/resize commits AND the
            // Figure-without-MCID rescue path (see docstring). The rescue
            // path is a strict superset of the legacy rule because the IR
            // bbox is always at least as authoritative as the WASM bbox
            // when both exist (IR is sourced from /A[/Layout]/BBox or from
            // a user gesture; WASM is MCID-derived and degenerate when MCR
            // descendants are absent).
            const override = irBboxById.get(e.structId);
            if (override) {
                out.push({ structId: e.structId, page: override.page, bbox: override.bbox });
            }
            else {
                // Rule 2: pass WASM entry through unchanged.
                out.push(e);
            }
        }
    }
    // Rule 3: append IR-side inserts not yet reflected in WASM bbox cache.
    const wantedPage = Number.isFinite(pageNum) ? pageNum : null;
    for (const [id, info] of irBboxById.entries()) {
        if (seenIds.has(id))
            continue;
        if (wantedPage !== null && info.page !== wantedPage)
            continue;
        out.push({ structId: id, page: info.page, bbox: info.bbox });
    }
    return out;
}
/**
 * Build a structId → {page, bbox} index for IR blocks that carry a local
 * bbox override (from drag/resize commits or UNDEFINED inserts).
 *
 * Only blocks with a non-null bbox are indexed — blocks without a bbox
 * in the IR are handled by rule 2 (pass WASM entry through unchanged).
 *
 * Recurses into Lists so nested LIs / table cells survive the lookup.
 */
function buildIrBboxIndex(ir) {
    const out = new Map();
    function visit(block) {
        if (!block)
            return;
        const rawBbox = block.bbox;
        const bboxMaybe = rawBbox && typeof rawBbox.x === "number"
            ? rawBbox
            : undefined;
        if (block.structId && bboxMaybe) {
            out.set(block.structId, {
                page: block.page || 1,
                bbox: [bboxMaybe.x, bboxMaybe.y, bboxMaybe.w, bboxMaybe.h],
            });
        }
        if (block.kind === "List" && Array.isArray(block.items)) {
            for (const item of block.items) {
                if (Array.isArray(item.content)) {
                    for (const ib of item.content)
                        visit(ib);
                }
            }
        }
    }
    for (const b of ir.blocks)
        visit(b);
    return out;
}
